import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { ElectronAPI } from '../types/electron';

interface ElectronContextType {
  isElectron: boolean;
  electronAPI: typeof window.electronAPI | undefined;
  isLoading: boolean;
}

const ElectronContext = createContext<ElectronContextType | undefined>(
  undefined
);

interface ElectronProviderProps {
  children: ReactNode;
}

export const ElectronProvider: React.FC<ElectronProviderProps> = ({
  children,
}) => {
  const [isElectron, setIsElectron] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // Check if we're running in Electron
    const checkElectron = () => {
      const hasElectronAPI = !!(
        window as Window & { electronAPI?: ElectronAPI }
      ).electronAPI;
      setIsElectron(hasElectronAPI);
      setIsLoading(false);
    };

    // Check immediately
    checkElectron();

    // Also check after a short delay to handle any delayed loading
    const timer = setTimeout(checkElectron, 100);
    return () => clearTimeout(timer);
  }, []);

  const value: ElectronContextType = {
    isElectron,
    electronAPI: window.electronAPI,
    isLoading,
  };

  return (
    <ElectronContext.Provider value={value}>
      {children}
    </ElectronContext.Provider>
  );
};

export const useElectron = (): ElectronContextType => {
  const context = useContext(ElectronContext);
  if (context === undefined) {
    throw new Error('useElectron must be used within an ElectronProvider');
  }
  return context;
};

// Convenience hooks for common use cases
export const useIsElectron = (): boolean => {
  const { isElectron } = useElectron();
  return isElectron;
};

export const useElectronAPI = () => {
  const { electronAPI } = useElectron();
  return electronAPI;
};

export const useIsLoading = (): boolean => {
  const { isLoading } = useElectron();
  return isLoading;
};

//
export const useIsBrowser = (): boolean => {
  const { isElectron } = useElectron();
  return !isElectron;
};
